import { intercomService } from "@services/intercomService";
import {
  WSAvatar,
  WSButton,
  WSCard,
  WSFlexBox,
  WSForm,
  WSImage,
  WSInfoBox,
  WSInputMask,
  WSList,
  WSText,
  toWSDateString,
  useWSModal,
  useWSSnackbar
} from "@wingspanhq/fe-component-library";
import { ExternalBankAccount } from "@wingspanhq/wingspan-api-sdk";
import * as Yup from "yup";
import imageTransactions from "../../../assets/images/microDepositTransactions.png";
import { ErrorAlert } from "@components/ErrorAlert";
import { queriesExternalBankAccounts } from "@query";

function amountToNumber(amount: string | null) {
  if (!amount) {
    return 0;
  }

  return Number(amount.replace("$0.", "0."));
}

type Props = {
  externalBankAccount: ExternalBankAccount;
  onClose: () => void;
};

type FormData = { amount1: string | null; amount2: string | null };

export const ModalMicrodepositsVerify: React.FC<Props> = ({
  externalBankAccount,
  onClose
}) => {
  const { openSnackbar } = useWSSnackbar();

  const [verify, verifyMeta] =
    queriesExternalBankAccounts.verifyExternalBankAccount.useMutation({
      onSuccess: () => {
        openSnackbar({
          type: "success",
          icon: {
            name: "check-circle"
          },
          message: "You’re all set! The payment account was verified"
        });
        onClose();
      }
    });

  return (
    <WSCard header="Verify Account" divider>
      <WSForm<FormData>
        defaultValues={{ amount1: null, amount2: null }}
        validationSchema={Yup.object().shape({
          amount1: Yup.string()
            .required("Should be greater than 0")
            .not(["00"], "Should be greater than 0"),
          amount2: Yup.string()
            .required("Should be greater than 0")
            .not(["00"], "Should be greater than 0")
        })}
        onSubmit={data => {
          verify({
            bankAccountId: externalBankAccount.id,
            microDepositVerification: {
              amounts: [
                amountToNumber(data.amount1),
                amountToNumber(data.amount2)
              ]
            }
          });
        }}
      >
        <WSList>
          <WSText.ParagraphSm>
            Wingspan made <b>two deposits</b> in USD into your account ending in
            •••
            {externalBankAccount.accountNumberMask} on{" "}
            {toWSDateString(new Date(), "monthDate")}. Enter the amounts to
            verify your account.
          </WSText.ParagraphSm>

          <WSInfoBox>
            <WSFlexBox.CenterY mb="XL">
              <WSAvatar.Icon
                mr="M"
                icon="bank"
                color="white"
                colorBackground="gray500"
              />

              <WSText>
                •••{externalBankAccount.accountNumberMask} Bank Statement
              </WSText>
            </WSFlexBox.CenterY>

            <WSImage mb="2XL" src={imageTransactions} placeholder="" />

            <p>Enter amounts:</p>

            <WSList>
              <WSForm.Field
                name="amount1"
                component={WSInputMask}
                componentProps={{
                  mask: "$0.99",
                  placeholder: "$0.00",
                  slotChar: "0"
                }}
              />

              <WSForm.Field
                name="amount2"
                component={WSInputMask}
                componentProps={{
                  mask: "$0.99",
                  placeholder: "$0.00",
                  slotChar: "0"
                }}
              />
            </WSList>
          </WSInfoBox>

          <ErrorAlert
            context="BankAccountVerification"
            error={verifyMeta.error}
          />

          <WSForm.Values names={["amount1", "amount2"]}>
            {values => {
              const isAmount1Valid =
                !!values.amount1 && values.amount1 !== "00";
              const isAmount2Valid =
                !!values.amount2 && values.amount2 !== "00";
              const isValid = isAmount1Valid && isAmount2Valid;

              return (
                <WSButton
                  fullWidth
                  disabled={!isValid}
                  loading={verifyMeta.isLoading}
                >
                  Verify bank account
                </WSButton>
              );
            }}
          </WSForm.Values>

          <WSText.ParagraphSm color="gray500">
            Payments to invoices made from this account will be in pending
            status until the verification process is complete. Once the account
            is verified, invoices paid with this account will be processed.
          </WSText.ParagraphSm>

          <WSText.ParagraphXs color="gray400">
            Didn’t receive the deposits?{" "}
            <WSButton.Link
              size="S"
              onClick={intercomService.open}
              type="button"
            >
              Contact support
            </WSButton.Link>
          </WSText.ParagraphXs>
        </WSList>
      </WSForm>
    </WSCard>
  );
};

export const useModalMicrodepositsVerify = () =>
  useWSModal(ModalMicrodepositsVerify, {
    style: {
      padding: 0,
      borderRadius: 8
    }
  });
